/**
 * ============================================================================
 * Iron Meridian - Post-Game Analytics Component
 * ============================================================================
 * 
 * Displays comprehensive performance analysis at game end:
 * - Multi-axis scores (8 dimensions)
 * - Performance timelines with turning points
 * - Strategic summary with narrative
 * - Comparative benchmarks
 * - Rank & title assignment
 * - Replay integration
 */

import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';

export default function PostGameAnalytics({ gameId, onClose, onJumpToTurn, isOpen }) {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [expandedSections, setExpandedSections] = useState({
    axes: true,
    timeline: false,
    summary: true,
    benchmarks: false
  });

  useEffect(() => {
    if (isOpen && gameId) {
      fetchAnalytics();
    }
  }, [isOpen, gameId]);

  async function fetchAnalytics() {
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch('/api/game/analytics', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ gameId })
      });
      
      if (!response.ok) {
        throw new Error('Failed to load analytics');
      }
      
      const result = await response.json();
      setAnalytics(result.data);
    } catch (err) {
      console.error('[ANALYTICS UI] Error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  function toggleSection(section) {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  }

  if (!isOpen) return null;

  const modal = (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
      <div className="bg-war-room-900 border border-war-room-600/30 rounded-2xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
        
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-war-room-600/30 bg-gradient-to-r from-war-room-800/50 to-war-room-900/50">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-600 to-purple-700 flex items-center justify-center shadow-lg">
              <span className="text-2xl">📊</span>
            </div>
            <div>
              <h2 className="text-2xl font-bold text-white">Post-Game Analytics</h2>
              <p className="text-sm text-gray-400">Performance Report & Strategic Analysis</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-war-room-700 rounded-lg transition-colors"
            title="Close"
          >
            <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {loading && (
            <div className="flex flex-col items-center justify-center py-20">
              <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-500 mb-4"></div>
              <p className="text-gray-400">Analyzing performance...</p>
            </div>
          )}

          {error && (
            <div className="bg-red-900/20 border border-red-600/30 rounded-lg p-4">
              <p className="text-red-400">Error: {error}</p>
              <button 
                onClick={fetchAnalytics}
                className="mt-2 text-sm text-red-300 hover:text-red-200 underline"
              >
                Retry
              </button>
            </div>
          )}

          {analytics && !loading && (
            <>
              {/* Rank & Title Card */}
              <RankCard rank={analytics.rank} finalScore={analytics.finalScore} outcome={analytics.outcome} />

              {/* Navigation Tabs */}
              <div className="flex gap-2 border-b border-war-room-600/30">
                <TabButton 
                  label="Overview" 
                  isActive={activeTab === 'overview'} 
                  onClick={() => setActiveTab('overview')} 
                />
                <TabButton 
                  label="Score Breakdown" 
                  isActive={activeTab === 'scores'} 
                  onClick={() => setActiveTab('scores')} 
                />
                <TabButton 
                  label="Timeline" 
                  isActive={activeTab === 'timeline'} 
                  onClick={() => setActiveTab('timeline')} 
                />
                <TabButton 
                  label="Summary" 
                  isActive={activeTab === 'summary'} 
                  onClick={() => setActiveTab('summary')} 
                />
              </div>

              {/* Tab Content */}
              {activeTab === 'overview' && (
                <OverviewTab 
                  analytics={analytics} 
                  expandedSections={expandedSections}
                  toggleSection={toggleSection}
                />
              )}

              {activeTab === 'scores' && (
                <ScoresTab 
                  axes={analytics.axes} 
                  benchmarks={analytics.benchmarks}
                />
              )}

              {activeTab === 'timeline' && (
                <TimelineTab 
                  timelines={analytics.timelines} 
                  onJumpToTurn={onJumpToTurn}
                />
              )}

              {activeTab === 'summary' && (
                <SummaryTab 
                  summary={analytics.summary}
                  axes={analytics.axes}
                />
              )}
            </>
          )}
        </div>

        {/* Footer Actions */}
        <div className="flex items-center justify-between p-6 border-t border-war-room-600/30 bg-war-room-800/30">
          <div className="text-xs text-gray-500">
            {analytics && `Game: ${gameId} • Turn ${analytics.turn} • Generated ${new Date(analytics.generatedAt).toLocaleTimeString()}`}
          </div>
          <div className="flex gap-3">
            {onJumpToTurn && (
              <button
                onClick={() => onJumpToTurn(0)}
                className="px-4 py-2 bg-war-room-700 hover:bg-war-room-600 rounded-lg transition-colors text-sm"
              >
                View Replay
              </button>
            )}
            <button
              onClick={onClose}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors text-sm font-medium"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}

// ============================================================================
// SUB-COMPONENTS
// ============================================================================

function TabButton({ label, isActive, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`px-4 py-2 text-sm font-medium transition-colors relative ${
        isActive 
          ? 'text-blue-400' 
          : 'text-gray-400 hover:text-gray-300'
      }`}
    >
      {label}
      {isActive && (
        <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-500" />
      )}
    </button>
  );
}

function RankCard({ rank, finalScore, outcome }) {
  const rankColors = {
    S: 'from-yellow-500 to-orange-600',
    A: 'from-blue-500 to-purple-600',
    B: 'from-green-500 to-teal-600',
    C: 'from-gray-500 to-slate-600',
    D: 'from-gray-600 to-gray-700',
    F: 'from-red-600 to-red-800'
  };

  const outcomeLabel = {
    victory: '🏆 Victory',
    defeat: '💀 Defeat',
    draw: '🤝 Draw',
    active: '⏸️ In Progress'
  };

  return (
    <div className={`bg-gradient-to-br ${rankColors[rank.rank] || rankColors.C} rounded-xl p-8 text-white shadow-2xl`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-6">
          <div className="text-8xl font-bold opacity-90">{rank.rank}</div>
          <div>
            <div className="text-3xl font-bold mb-2">{rank.title}</div>
            <div className="text-lg opacity-90">{outcomeLabel[outcome] || outcome}</div>
          </div>
        </div>
        <div className="text-right">
          <div className="text-sm opacity-80 mb-1">Final Score</div>
          <div className="text-5xl font-bold">{finalScore.final.toFixed(0)}</div>
          <div className="text-sm opacity-80 mt-1">out of 100</div>
        </div>
      </div>
    </div>
  );
}

function OverviewTab({ analytics, expandedSections, toggleSection }) {
  return (
    <div className="space-y-4">
      {/* Score Summary Grid */}
      <div className="grid grid-cols-4 gap-4">
        {Object.entries(analytics.axes).map(([axis, data]) => (
          <ScoreCard key={axis} axis={axis} data={data} />
        ))}
      </div>

      {/* Key Insights */}
      <InsightCard 
        title="Strategic Summary"
        icon="🎯"
        expanded={expandedSections.summary}
        onToggle={() => toggleSection('summary')}
      >
        <div className="space-y-3">
          <InsightItem 
            label="Playstyle" 
            value={analytics.summary.playstyleLabel} 
          />
          <InsightItem 
            label="Dominant Strategy" 
            value={analytics.summary.dominantStrategy.description} 
          />
          {analytics.summary.majorSuccess && (
            <InsightItem 
              label="Major Success" 
              value={analytics.summary.majorSuccess.description}
              positive 
            />
          )}
          {analytics.summary.criticalFailure && (
            <InsightItem 
              label="Critical Failure" 
              value={analytics.summary.criticalFailure.description}
              negative 
            />
          )}
        </div>
      </InsightCard>

      {/* Benchmarks */}
      <InsightCard 
        title="Performance vs Expected"
        icon="📈"
        expanded={expandedSections.benchmarks}
        onToggle={() => toggleSection('benchmarks')}
      >
        <BenchmarkSummary benchmarks={analytics.benchmarks} />
      </InsightCard>
    </div>
  );
}

function ScoresTab({ axes, benchmarks }) {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-6">
        {Object.entries(axes).map(([axis, data]) => (
          <DetailedScoreCard 
            key={axis} 
            axis={axis} 
            data={data}
            benchmark={benchmarks[axis]}
          />
        ))}
      </div>
    </div>
  );
}

function TimelineTab({ timelines, onJumpToTurn }) {
  return (
    <div className="space-y-6">
      {Object.entries(timelines).map(([metric, timeline]) => (
        <TimelineChart 
          key={metric} 
          metric={metric} 
          timeline={timeline}
          onJumpToTurn={onJumpToTurn}
        />
      ))}
    </div>
  );
}

function SummaryTab({ summary, axes }) {
  return (
    <div className="space-y-6">
      <NarrativeBlock 
        title="Your Strategic Journey"
        icon="📜"
        content={[
          summary.dominantStrategy.description,
          summary.majorSuccess?.description,
          summary.criticalFailure?.description,
          summary.keyTurningPoint?.description,
          summary.missedOpportunities?.description
        ].filter(Boolean)}
      />

      <div className="grid grid-cols-2 gap-6">
        <StatDistribution axes={axes} />
        <PlaystyleProfile summary={summary} axes={axes} />
      </div>
    </div>
  );
}

function ScoreCard({ axis, data }) {
  const icons = {
    military: '⚔️',
    economic: '💰',
    technological: '🔬',
    diplomatic: '🤝',
    stability: '🏛️',
    crisis: '🔥',
    doctrine: '📖',
    adaptability: '🔄'
  };

  const labelColors = {
    Exceptional: 'text-yellow-400',
    Strong: 'text-green-400',
    Moderate: 'text-blue-400',
    Weak: 'text-orange-400',
    Poor: 'text-red-400'
  };

  return (
    <div className="bg-war-room-800/50 rounded-lg p-4 border border-war-room-600/30">
      <div className="flex items-center gap-2 mb-2">
        <span className="text-xl">{icons[axis]}</span>
        <span className="text-xs font-medium text-gray-400 capitalize">{axis}</span>
      </div>
      <div className="text-2xl font-bold text-white mb-1">{data.score.toFixed(0)}</div>
      <div className={`text-xs font-medium ${labelColors[data.label]}`}>{data.label}</div>
    </div>
  );
}

function DetailedScoreCard({ axis, data, benchmark }) {
  const icons = {
    military: '⚔️',
    economic: '💰',
    technological: '🔬',
    diplomatic: '🤝',
    stability: '🏛️',
    crisis: '🔥',
    doctrine: '📖',
    adaptability: '🔄'
  };

  return (
    <div className="bg-war-room-800/50 rounded-lg p-6 border border-war-room-600/30">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <span className="text-3xl">{icons[axis]}</span>
          <div>
            <h3 className="font-bold text-white capitalize">{axis}</h3>
            <p className="text-xs text-gray-400">{data.description}</p>
          </div>
        </div>
        <div className="text-right">
          <div className="text-3xl font-bold text-white">{data.score.toFixed(0)}</div>
          <div className={`text-sm font-medium ${
            data.label === 'Exceptional' ? 'text-yellow-400' :
            data.label === 'Strong' ? 'text-green-400' :
            data.label === 'Moderate' ? 'text-blue-400' :
            data.label === 'Weak' ? 'text-orange-400' :
            'text-red-400'
          }`}>{data.label}</div>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="relative h-2 bg-war-room-700 rounded-full overflow-hidden">
        <div 
          className="absolute left-0 top-0 h-full bg-gradient-to-r from-blue-600 to-purple-600 transition-all"
          style={{ width: `${data.score}%` }}
        />
        {benchmark && (
          <div 
            className="absolute top-0 w-0.5 h-full bg-yellow-500"
            style={{ left: `${benchmark.baseline}%` }}
            title={`Expected: ${benchmark.baseline.toFixed(0)}`}
          />
        )}
      </div>

      {benchmark && (
        <div className="mt-2 text-xs text-gray-400">
          Expected: {benchmark.baseline.toFixed(0)} • 
          <span className={benchmark.difference > 0 ? 'text-green-400' : 'text-red-400'}>
            {' '}{benchmark.difference > 0 ? '+' : ''}{benchmark.difference.toFixed(0)}
          </span>
        </div>
      )}
    </div>
  );
}

function TimelineChart({ metric, timeline, onJumpToTurn }) {
  const maxValue = Math.max(...timeline.dataPoints.map(p => p.value), 1);
  const normalizedPoints = timeline.dataPoints.map(p => ({
    ...p,
    normalized: (p.value / maxValue) * 100
  }));

  return (
    <div className="bg-war-room-800/50 rounded-lg p-6 border border-war-room-600/30">
      <h3 className="font-bold text-white capitalize mb-4">{metric} Timeline</h3>
      
      {/* Simple SVG Line Chart */}
      <div className="relative h-32 bg-war-room-900/50 rounded-lg p-4">
        <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
          {/* Grid lines */}
          {[0, 25, 50, 75, 100].map(y => (
            <line key={y} x1="0" y1={y} x2="100" y2={y} stroke="#374151" strokeWidth="0.5" />
          ))}
          
          {/* Data line */}
          <polyline
            points={normalizedPoints.map((p, i) => 
              `${(i / (normalizedPoints.length - 1)) * 100},${100 - p.normalized}`
            ).join(' ')}
            fill="none"
            stroke="#3b82f6"
            strokeWidth="2"
          />
          
          {/* Turning points */}
          {timeline.turningPoints.map((tp, i) => {
            const pointIndex = normalizedPoints.findIndex(p => p.turn === tp.turn);
            if (pointIndex === -1) return null;
            const x = (pointIndex / (normalizedPoints.length - 1)) * 100;
            const y = 100 - normalizedPoints[pointIndex].normalized;
            
            return (
              <circle
                key={i}
                cx={x}
                cy={y}
                r="2"
                fill={tp.type === 'surge' ? '#10b981' : '#ef4444'}
                onClick={() => onJumpToTurn?.(tp.turn)}
                style={{ cursor: 'pointer' }}
              />
            );
          })}
        </svg>
      </div>

      {/* Turning Points List */}
      {timeline.turningPoints.length > 0 && (
        <div className="mt-4 space-y-2">
          {timeline.turningPoints.map((tp, i) => (
            <button
              key={i}
              onClick={() => onJumpToTurn?.(tp.turn)}
              className="text-xs text-left w-full px-3 py-2 bg-war-room-700/50 hover:bg-war-room-700 rounded transition-colors"
            >
              <span className={tp.type === 'surge' ? 'text-green-400' : 'text-red-400'}>
                Turn {tp.turn}
              </span>
              {' '}• {tp.type} ({(tp.magnitude * 100).toFixed(0)}%)
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function InsightCard({ title, icon, expanded, onToggle, children }) {
  return (
    <div className="bg-war-room-800/50 rounded-lg border border-war-room-600/30 overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between p-4 hover:bg-war-room-700/30 transition-colors"
      >
        <div className="flex items-center gap-3">
          <span className="text-2xl">{icon}</span>
          <h3 className="font-bold text-white">{title}</h3>
        </div>
        <svg 
          className={`w-5 h-5 text-gray-400 transition-transform ${expanded ? 'rotate-180' : ''}`}
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {expanded && (
        <div className="p-4 pt-0">
          {children}
        </div>
      )}
    </div>
  );
}

function InsightItem({ label, value, positive, negative }) {
  return (
    <div className="flex items-start gap-3">
      <div className={`text-xs font-medium ${positive ? 'text-green-400' : negative ? 'text-red-400' : 'text-gray-400'} min-w-[120px]`}>
        {label}
      </div>
      <div className="text-sm text-white flex-1">{value}</div>
    </div>
  );
}

function BenchmarkSummary({ benchmarks }) {
  const overall = benchmarks.overall;
  
  return (
    <div className="space-y-4">
      <div className={`p-4 rounded-lg ${
        overall.status === 'exceeded' ? 'bg-green-900/20 border border-green-600/30' :
        overall.status === 'underperformed' ? 'bg-red-900/20 border border-red-600/30' :
        'bg-blue-900/20 border border-blue-600/30'
      }`}>
        <div className="text-lg font-bold text-white mb-2">
          {overall.status === 'exceeded' ? '🎉 Exceeded Expectations!' :
           overall.status === 'underperformed' ? '📉 Below Expected' :
           '✓ Met Expectations'}
        </div>
        <div className="text-sm text-gray-300">
          Your score: {overall.playerScore.toFixed(0)} • Expected: {overall.expectedScore.toFixed(0)} • 
          Difference: <span className={overall.difference > 0 ? 'text-green-400' : 'text-red-400'}>
            {overall.difference > 0 ? '+' : ''}{overall.difference.toFixed(0)}
          </span>
        </div>
      </div>
    </div>
  );
}

function NarrativeBlock({ title, icon, content }) {
  return (
    <div className="bg-war-room-800/50 rounded-lg p-6 border border-war-room-600/30">
      <div className="flex items-center gap-3 mb-4">
        <span className="text-3xl">{icon}</span>
        <h3 className="font-bold text-white text-xl">{title}</h3>
      </div>
      <div className="space-y-3">
        {content.map((text, i) => (
          <p key={i} className="text-gray-300 leading-relaxed">{text}</p>
        ))}
      </div>
    </div>
  );
}

function StatDistribution({ axes }) {
  const sortedAxes = Object.entries(axes)
    .sort((a, b) => b[1].score - a[1].score);

  return (
    <div className="bg-war-room-800/50 rounded-lg p-6 border border-war-room-600/30">
      <h3 className="font-bold text-white mb-4">Score Distribution</h3>
      <div className="space-y-3">
        {sortedAxes.map(([axis, data]) => (
          <div key={axis}>
            <div className="flex items-center justify-between text-sm mb-1">
              <span className="text-gray-300 capitalize">{axis}</span>
              <span className="text-white font-medium">{data.score.toFixed(0)}</span>
            </div>
            <div className="h-2 bg-war-room-700 rounded-full overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-blue-600 to-purple-600"
                style={{ width: `${data.score}%` }}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function PlaystyleProfile({ summary, axes }) {
  return (
    <div className="bg-war-room-800/50 rounded-lg p-6 border border-war-room-600/30">
      <h3 className="font-bold text-white mb-4">Playstyle Profile</h3>
      <div className="space-y-4">
        <div className="text-center py-6">
          <div className="text-4xl mb-2">🎭</div>
          <div className="text-xl font-bold text-white">{summary.playstyleLabel}</div>
        </div>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-400">Primary Focus</span>
            <span className="text-white capitalize">{summary.dominantStrategy.axis}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-400">Consistency</span>
            <span className="text-white">{axes.doctrine.label}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-400">Adaptability</span>
            <span className="text-white">{axes.adaptability.label}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
